import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import "./AdminForm.css";
import { API_BASE_URL, getSession } from "./apiConfig";

const AwbIn = () => {
  const session = getSession();
  const userRole = session?.Role || "";

  const [activeTab, setActiveTab] = useState("entry");
  const [parties, setParties] = useState([]); // Party Master list
  const [companies, setCompanies] = useState([]); // CourierCompany list
  const [awbList, setAwbList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const emptyForm = {
    id: null,
    AWB_NO: "",
    AWB_Dt: new Date().toISOString().slice(0, 10),
    AC_Code: "",
    partySearchText: "",
    Qty: "",
    Weight: "",
    Remarks: "",
    ManifestCourierCompanyId: "",
    courierSearchText: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchParties();
    fetchCompanies();
    fetchAwbs();
  }, []);

  const fetchParties = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/party`);
      const data = await res.json();
      setParties(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading parties", err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/courier-company`);
      const data = await res.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading courier companies", err);
    }
  };

  const fetchAwbs = async (search = "") => {
    try {
      const url = search
        ? `${API_BASE_URL}/awb?search=${encodeURIComponent(search)}`
        : `${API_BASE_URL}/awb`;
      const res = await fetch(url);
      const data = await res.json();
      setAwbList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading AWBs", err);
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    fetchAwbs(val);
  };

  const handlePartyTextChange = (e) => {
    const text = e.target.value;
    const match = parties.find(
      (p) => p.partyName.toLowerCase() === text.toLowerCase()
    );
    setForm({
      ...form,
      partySearchText: text,
      AC_Code: match ? match.partyId : "",
    });
  };

  const handleCourierTextChange = (e) => {
    const text = e.target.value;
    const match = companies.find(
      (c) => c.companyName.toLowerCase() === text.toLowerCase()
    );
    setForm({
      ...form,
      courierSearchText: text,
      ManifestCourierCompanyId: match ? match.id : "",
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (!form.AWB_NO || !form.AC_Code) {
      setMessage({ type: "error", text: "AWB No. and a valid Party (selected from the list) are required." });
      return;
    }

    // NOTE: this payload still uses PascalCase keys (AWB_NO, AC_Code, ...) because
    // it's deserialized into AwbInModelClass on the backend, which matches
    // property names case-insensitively regardless of the output JSON casing issue.
    const payload = {
      AWB_NO: form.AWB_NO,
      AWB_Dt: form.AWB_Dt,
      AC_Code: Number(form.AC_Code),
      Qty: form.Qty ? Number(form.Qty) : 0,
      Weight: form.Weight ? Number(form.Weight) : 0,
      Remarks: form.Remarks,
      ManifestCourierCompanyId: form.ManifestCourierCompanyId
        ? Number(form.ManifestCourierCompanyId)
        : null,
    };

    try {
      let res;
      if (isEditing) {
        res = await fetch(`${API_BASE_URL}/awb/${form.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "X-User-Role": userRole },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_BASE_URL}/awb`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) throw new Error("Save failed");

      setMessage({ type: "success", text: isEditing ? "AWB updated successfully." : "AWB added successfully." });
      resetForm();
      fetchAwbs(searchTerm);
      setActiveTab("view");
    } catch (err) {
      setMessage({ type: "error", text: "Error saving AWB entry." });
    }
  };

  // NOTE: `awb` here comes from GET /api/awb, which now returns explicit
  // camelCase keys (awbNo, awbDt, acCode, qty, weight, remarks, manifestCourierCompanyId).
  const handleEdit = (awb) => {
    const partyName = parties.find((p) => p.partyId === awb.acCode)?.partyName || "";
    const courierName = companies.find((c) => c.id === awb.manifestCourierCompanyId)?.companyName || "";

    setForm({
      id: awb.id,
      AWB_NO: awb.awbNo,
      AWB_Dt: awb.awbDt ? awb.awbDt.slice(0, 10) : new Date().toISOString().slice(0, 10),
      AC_Code: awb.acCode,
      partySearchText: partyName,
      Qty: awb.qty ?? "",
      Weight: awb.weight ?? "",
      Remarks: awb.remarks ?? "",
      ManifestCourierCompanyId: awb.manifestCourierCompanyId ?? "",
      courierSearchText: courierName,
    });
    setIsEditing(true);
    setMessage({ type: "", text: "" });
    setActiveTab("entry");
  };

  const handleDelete = async (id) => {
    if (userRole !== "Admin") {
      setMessage({ type: "error", text: "Only Admin users can delete records." });
      return;
    }
    if (!window.confirm("Delete this AWB entry?")) return;

    try {
      const res = await fetch(`${API_BASE_URL}/awb/${id}`, {
        method: "DELETE",
        headers: { "X-User-Role": userRole },
      });
      if (!res.ok) throw new Error("Delete failed");
      setMessage({ type: "success", text: "AWB deleted successfully." });
      fetchAwbs(searchTerm);
    } catch (err) {
      setMessage({ type: "error", text: "Error deleting AWB entry." });
    }
  };

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">AWB In Entry</h1>
      </div>

      <div className="content-card">
        <div className="tab-container">
          <button
            className={`tab-btn ${activeTab === "entry" ? "active" : ""}`}
            onClick={() => setActiveTab("entry")}
          >
            {isEditing ? "Edit Entry" : "Create New Entry"}
          </button>
          <button
            className={`tab-btn ${activeTab === "view" ? "active" : ""}`}
            onClick={() => setActiveTab("view")}
          >
            View Records ({awbList.length})
          </button>
        </div>

        {activeTab === "entry" && (
          <div className="form-wrapper">
            {message.text && (
              <div className={`alert ${message.type === "success" ? "alert-success" : "alert-error"}`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>AWB No. *</label>
                  <input type="text" name="AWB_NO" className="form-control" value={form.AWB_NO} onChange={handleChange} required />
                </div>

                <div className="form-group">
                  <label>AWB Date</label>
                  <input type="date" name="AWB_Dt" className="form-control" value={form.AWB_Dt} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Party (Account) — type to search *</label>
                  <input
                    type="text"
                    list="party-options"
                    className="form-control"
                    value={form.partySearchText}
                    onChange={handlePartyTextChange}
                    placeholder="Start typing party name..."
                  />
                  <datalist id="party-options">
                    {parties.map((p) => (
                      <option key={p.partyId} value={p.partyName} />
                    ))}
                  </datalist>
                  {form.partySearchText && !form.AC_Code && (
                    <span style={{ fontSize: "0.75rem", color: "#dc2626" }}>
                      No matching party — pick one from the list, or add it in Party Master first.
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>Qty</label>
                  <input type="number" name="Qty" className="form-control" value={form.Qty} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input type="number" step="0.01" name="Weight" className="form-control" value={form.Weight} onChange={handleChange} />
                </div>

                <div className="form-group">
                  <label>Diverted to Courier Co. — type to search</label>
                  <input
                    type="text"
                    list="courier-options"
                    className="form-control"
                    value={form.courierSearchText}
                    onChange={handleCourierTextChange}
                    placeholder="Start typing courier company..."
                  />
                  <datalist id="courier-options">
                    {companies.map((c) => (
                      <option key={c.id} value={c.companyName} />
                    ))}
                  </datalist>
                </div>

                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Remarks</label>
                  <input type="text" name="Remarks" className="form-control" value={form.Remarks} onChange={handleChange} />
                </div>
              </div>

              <button type="submit" className="btn-submit">
                {isEditing ? "Update AWB" : "Add AWB"}
              </button>
              {isEditing && (
                <button type="button" className="btn-secondary-sm" style={{ marginLeft: "10px" }} onClick={resetForm}>
                  Cancel Edit
                </button>
              )}
            </form>
          </div>
        )}

        {activeTab === "view" && (
          <div className="table-wrap">
            <div style={{ padding: "16px 24px 0" }}>
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: "400px" }}
                placeholder="Search by AWB No. or AC Code..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>AWB No.</th>
                  <th>Date</th>
                  <th>Party</th>
                  <th>Qty</th>
                  <th>Weight</th>
                  <th>Courier Co.</th>
                  <th>Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {awbList.length > 0 ? (
                  awbList.map((awb) => {
                    const partyName = parties.find((p) => p.partyId === awb.acCode)?.partyName || awb.acCode;
                    const courierName = companies.find((c) => c.id === awb.manifestCourierCompanyId)?.companyName || "-";
                    return (
                      <tr key={awb.id}>
                        <td><strong>{awb.awbNo}</strong></td>
                        <td>{awb.awbDt ? new Date(awb.awbDt).toLocaleDateString() : "-"}</td>
                        <td>{partyName}</td>
                        <td>{awb.qty}</td>
                        <td>{awb.weight}</td>
                        <td>{courierName}</td>
                        <td>{awb.remarks}</td>
                        <td>
                          <button className="btn-secondary-sm" onClick={() => handleEdit(awb)}>Edit</button>
                          <button
                            className="btn-secondary-sm"
                            style={{ marginLeft: "6px", color: "#dc2626", borderColor: "#fecaca" }}
                            onClick={() => handleDelete(awb.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="empty-state">
                      <div style={{ marginBottom: "8px", fontSize: "1.2rem" }}>📦</div>
                      No AWB records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AwbIn;